// Anita Week 4 / Day 2 — Directions and Turns. U.S. Grade-3 math with route
// tables, path totals, 1/2 fraction, and time-comparison logic.
// Print includes ready-to-use step tables and time-comparison tables.

const day2 = {
  id: 'day-2',
  studentId: 'anita',
  weekId: 'week-4',
  dayId: 'day-2',
  status: 'active',
  title: 'Направления и повороты / Directions and Turns',
  shortTitle: 'Направления / Directions',
  theme:
    'Анита учится понимать направления, повороты, короткий и длинный путь. / Anita learns directions, turns, short and long routes.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'map', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни показывает Аните узкие тропинки и повороты. Иногда путь короче, но с ожиданием у перехода. Иногда длиннее, но безопасный. Сегодня Анита учится сравнивать пути. / Nimny shows narrow paths and turns. Sometimes the shorter path has a wait. Sometimes the longer one is safer. Today Anita compares paths.',
  goals: [
    'Считать шаги и переводить в метры. / Count steps and convert to meters.',
    'Работать с половиной маршрута. / Work with half a route.',
    'Сравнивать пути по времени. / Compare paths by time.',
    'Найти логическую ошибку про «короткий = быстрый». / Spot the "short means fast" fallacy.',
    'Написать простые русские слова направлений. / Write direction words in Russian.'
  ],
  vocabulary: [
    { term: 'вперёд / forward', definition: 'движение прямо. / straight movement.' },
    { term: 'назад / back', definition: 'обратное движение. / reverse.' },
    { term: 'направо / right', definition: 'поворот вправо. / turn right.' },
    { term: 'налево / left', definition: 'поворот влево. / turn left.' },
    { term: 'шаг / step', definition: 'один шаг ноги. / one foot step.' },
    { term: 'половина / half', definition: '1/2 части. / one half.' },
    { term: 'ждать / to wait', definition: 'стоять и не идти. / to stand and not walk.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Задание 1 — Слова направлений / Direction Words',
      prompt: 'Прочитай по-русски:\nвперёд\nназад\nнаправо\nналево\nшаг\nждать\nбыстро\nполовина',
      ruPrimaryText: 'Прочитай 8 слов направлений.',
      enHelpText: 'Read 8 direction words in Russian.',
      answer: 'Анита пробует читать русские слова.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Задание 2 — Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\nНаправления помогают объяснить путь.\nМожно идти вперёд, назад, направо и налево.\nХороший маршрут зависит не только от длины.\nИногда надо ждать, обходить или выбирать безопасную дорогу.\nПоэтому карту нужно читать внимательно.',
      ruPrimaryText: 'Прочитай 5 фраз про направления и путь.',
      enHelpText: 'Directions help explain the route. A good route is not only the length; sometimes you wait or go around.',
      answer: 'Анита пробует читать 5 фраз.'
    },
    {
      id: 'math-turns-and-steps',
      type: 'text',
      title: 'Задание 3 — Повороты и шаги / Turns and Steps',
      prompt:
        'Анита прошла 8 шагов вперёд, повернула направо и прошла ещё 6 шагов, потом вернулась назад на 3 шага.\n\nОтветь:\n1. Сложи все шаги.\n2. Сколько шагов она прошла всего?',
      ruPrimaryText: 'Сложи все шаги (включая шаги назад).',
      enHelpText: 'Even the steps back are counted as walked steps.',
      answer: '1) 8 + 6 + 3. 2) 17 шагов.'
    },
    {
      id: 'math-steps-meters',
      type: 'text',
      title: 'Задание 4 — Шаги и метры / Steps and Meters',
      prompt:
        'Один большой шаг Аниты равен 2 метрам.\nОна прошла 9 больших шагов до дерева и 7 больших шагов до лавочки.\n\nОтветь:\n1. Сколько шагов всего?\n2. Сколько метров всего?',
      ruPrimaryText: 'Двухшаговая задача: сложи шаги, потом умножь на 2 м.',
      enHelpText: 'Find total steps first, then multiply by 2.',
      answer: '1) 9 + 7 = 16 шагов. 2) 16 × 2 = 32 метра.'
    },
    {
      id: 'math-half-route',
      type: 'text',
      title: 'Задание 5 — Половина маршрута / Half the Route',
      prompt:
        'Весь маршрут до домика Нимни — 48 метров.\nАнита прошла 1/2 маршрута.\nПотом прошла ещё 10 метров.\n\nОтветь:\n1. Сколько метров в 1/2 маршрута?\n2. Сколько метров прошла всего?\n3. Сколько метров осталось?',
      ruPrimaryText: 'Дробь + сложение + остаток.',
      enHelpText: 'Find half of 48 first, then add 10, then subtract from 48.',
      answer: '1) 1/2 от 48 = 24. 2) 24 + 10 = 34. 3) 48 − 34 = 14 метров.'
    },
    {
      id: 'math-time-compare',
      type: 'text',
      title: 'Задание 6 — Какой путь быстрее / Which Path Is Faster',
      prompt:
        'Путь A занимает 18 минут.\nПуть B занимает 12 минут, но нужно ещё 7 минут ждать у перехода.\n\nОтветь:\n1. Полное время пути B?\n2. Какой путь быстрее?\n3. На сколько минут?',
      ruPrimaryText: 'Найди полное время пути B и сравни с A.',
      enHelpText: 'Add the waiting time to path B before comparing.',
      answer: '1) B: 12 + 7 = 19 минут. 2) A быстрее. 3) 19 − 18 = 1 минута.'
    },
    {
      id: 'logic-short-fast',
      type: 'text',
      title: 'Задание 7 — Логика: короче — быстрее? / Short = Fast?',
      prompt:
        'Нимни говорит: «Путь B короче, значит он всегда быстрее.»\n\nНайди ошибку. Что ещё может влиять на время? Ответь 1–3 предложениями.',
      ruPrimaryText: 'Найди ошибку в правиле «короче = быстрее».',
      enHelpText: 'Waiting, obstacles, safety can make a short path slower.',
      answer: 'Ожидаемо: «Ожидание, светофор, обход или пробка могут сделать короткий путь дольше.»'
    },
    {
      id: 'spelling-day2',
      type: 'text',
      title: 'Задание 8 — Правописание / Spelling',
      prompt:
        'Напиши:\nнаправо\nналево\nвперёд\nназад\nждать\n\nВставь буквы:\nна_раво\nна_ево\nвп_рёд\nн_зад\nж_ать',
      ruPrimaryText: 'Напиши 5 слов направлений и вставь буквы.',
      enHelpText: 'Write 5 direction words and fill in the missing letters.',
      answer: 'направо, налево, вперёд, назад, ждать.'
    },
    {
      id: 'creative-my-path',
      type: 'text',
      title: 'Задание 9 — Мой путь / My Path',
      prompt:
        'Напиши 2 предложения про свой путь:\n«Я иду ______. Потом я ______.»\nМожно про путь в магазин, к бабушке или на площадку (только вымышленный вариант).',
      ruPrimaryText: 'Напиши 2 предложения про свой путь.',
      enHelpText: 'Write 2 short sentences about a path.',
      answer: 'Open-ended. Ожидаемо 2 связные фразы с направлениями.'
    },
    {
      id: 'speech-day2',
      type: 'text',
      title: 'Задание 10 — Объясни родителю / Explain to Parent',
      prompt:
        'Скажи родителю по-русски:\nЯ иду вперёд.\nПотом поворачиваю направо.\nПотом иду назад.\nИногда нужно ждать.',
      ruPrimaryText: 'Объясни направления и ожидание по-русски.',
      enHelpText: 'Tell the parent: I go forward, turn right, go back, sometimes I wait.',
      answer: 'Accept if Anita uses direction words in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Начерти таблицу шагов и таблицу времени.',
    '2. Реши задачи 3–6.',
    '3. Напиши слова направлений.',
    '4. Расскажи родителю про повороты и ожидание.'
  ],
  printWorksheet: {
    title: 'Направления и повороты / Directions and Turns',
    subtitle: 'Неделя 4 · День 2 / Week 4 · Day 2',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Сегодня ты учишься понимать направления, повороты и время пути. / Today you learn directions, turns and travel time.' },
          { type: 'text', text: 'Задание 1 — Слова направлений. Прочитай и обведи:' },
          { type: 'list', items: [
            'вперёд / forward',
            'назад / back',
            'направо / right',
            'налево / left',
            'шаг / step',
            'ждать / to wait',
            'быстро / fast',
            'половина / half'
          ] },
          { type: 'text', text: 'Задание 2 — Маленькое чтение. Поставь галочку после чтения:' },
          { type: 'list', items: [
            '□ Направления помогают объяснить путь.',
            '□ Можно идти вперёд, назад, направо и налево.',
            '□ Хороший маршрут зависит не только от длины.',
            '□ Иногда надо ждать, обходить или выбирать безопасную дорогу.',
            '□ Поэтому карту нужно читать внимательно.'
          ] },
          { type: 'text', text: 'Задание 3 — Повороты и шаги. Готовая таблица:' },
          { type: 'list', items: [
            'Действие          | Шаги',
            'Вперёд            |  8',
            'Направо и вперёд  |  6',
            'Назад             |  3',
            'Всего шагов: ___'
          ] },
          { type: 'text', text: 'Задание 4 — Шаги и метры. Мини-карта:' },
          { type: 'list', items: [
            'Дом → дерево:    9 больших шагов',
            'Дерево → лавочка: 7 больших шагов',
            '1 шаг = 2 м',
            'Шагов всего: ___',
            'Метров всего: ___ (умножь на 2)'
          ] },
          { type: 'text', text: 'Задание 5 — Половина маршрута. Полоска маршрута:' },
          { type: 'list', items: [
            '[Дом] ────────────────── [Домик Нимни]',
            'Весь путь = 48 м',
            '1/2 пути = ___ м',
            'Потом ещё = 10 м',
            'Прошла всего = ___ м',
            'Осталось = ___ м'
          ] }
        ]
      },
      {
        title: 'Время пути / Time Comparison',
        blocks: [
          { type: 'text', text: 'Задание 6 — Какой путь быстрее. Готовая таблица времени:' },
          { type: 'list', items: [
            'Путь | Время идти | Ждать | Всего',
            '  A  |    18 мин  |  0    |  ___',
            '  B  |    12 мин  |  7    |  ___',
            'Быстрее: ___',
            'На сколько минут: ___'
          ] },
          { type: 'text', text: 'Задание 7 — Логика: короче — быстрее? Найди ошибку и напиши 1–3 предложения.' },
          { type: 'text', text: 'Ответ: __________________________________________________________________________________________________________________' }
        ]
      },
      {
        title: 'Русский и мой путь / Russian and My Path',
        blocks: [
          { type: 'text', text: 'Задание 8 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: [
            'направо   налево   вперёд   назад   ждать',
            'на_раво    на_ево    вп_рёд    н_зад    ж_ать'
          ] },
          { type: 'text', text: 'Задание 9 — Мой путь. Нарисуй маленькую схему-рисунок своего пути и напиши 2 предложения:' },
          { type: 'list', items: [
            'Я иду __________________________________________________.',
            'Потом я __________________________________________________.'
          ] },
          { type: 'text', text: 'Задание 10 — Объясни родителю. Скажи:' },
          { type: 'list', items: [
            '□ Я иду вперёд.',
            '□ Потом поворачиваю направо.',
            '□ Потом иду назад.',
            '□ Иногда нужно ждать.'
          ] },
          { type: 'text', text: 'Parent Check: □ прочитала слова  □ прочитала 5 фраз  □ таблица шагов  □ шаги и метры  □ половина маршрута  □ таблица времени  □ логика  □ 2 предложения про путь.' },
          { type: 'text', text: 'Notebook Mode fallback: если нет принтера, начерти таблицы шагов и времени в тетради и повтори задания 3–6.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита работает с направлениями, поворотами, шагами, дробью 1/2 и сравнивает время двух путей.',
    mathGoal: 'U.S. Grade 3: сложение шагов, умножение шагов на метры, 1/2 от целого, сравнение по полному времени.',
    russianGoal: 'Прочитать слова направлений и 5 фраз; написать 5 слов, вставить буквы.',
    readingWritingGoal: 'Написать 2 предложения про свой путь и найти логическую ошибку.',
    practicalGoal: 'Объяснить, что «короче» не всегда «быстрее».',
    parentTheoryCard: {
      title: 'Как объяснить время и путь',
      purpose: 'Показать, что полное время — это не только «идти», но и «ждать» и «обойти».',
      keyMessage: 'Полное время = время идти + время ждать.',
      parentScript: [
        '«Даже шаги назад считаются как пройденные шаги.»',
        '«1/2 от 48 — это 24. Это ровно половина маршрута.»',
        '«У пути B добавь ожидание: 12 + 7 = 19.»',
        '«Смотри, A всего 18. Значит A на 1 минуту быстрее.»'
      ],
      practiceSteps: [
        'Пройди с Анитой воображаемые 8 шагов вперёд, потом 6 направо, потом 3 назад.',
        'Проверь: 8 + 6 + 3 = 17.',
        'Скажи: «Один большой шаг ≈ 2 метра. Сколько метров?»',
        'В задании 5 покажи полоску маршрута и найдите 1/2 вместе.',
        'В задании 6 заполните таблицу времени.'
      ],
      doNotSay: [
        'Не решай за Аниту.',
        'Не требуй запомнить формулу.',
        'Не пропускай столбец «Ждать» в таблице времени.'
      ],
      paperOnlyNotes: [
        'Можно рисовать шаги стрелками ← → ↑ ↓.',
        'Полоску маршрута можно чертить одной линией.'
      ]
    },
    whatThisLessonTeaches: 'Двухшаговые задачи, дробь 1/2 от целого и сравнение путей по полному времени.',
    keyMessage: 'Короткий путь не всегда быстрый: считай ВСЁ время, включая ожидание.',
    howToExplain: 'Скажите: «Смотри на полное время, а не только на длину.»',
    exampleDialogue:
      'Parent: Всего шагов?\nChild: 17.\nParent: 16 шагов по 2 метра — сколько метров?\nChild: 32.\nParent: 1/2 от 48?\nChild: 24.\nParent: Какой путь быстрее?\nChild: A на 1 минуту.',
    readingSupport: 'Слово «половина» можно проговорить по слогам: по-ло-ви-на.',
    russianSpeechGoal: 'Анита говорит: «Я иду вперёд. Поворачиваю направо. Иногда нужно ждать.»',
    mathAnswerKey:
      'math-turns-and-steps: 8 + 6 + 3 = 17 шагов.\n' +
      'math-steps-meters: 9 + 7 = 16 шагов; 16 × 2 = 32 метра.\n' +
      'math-half-route: 1/2 от 48 = 24; 24 + 10 = 34; 48 − 34 = 14 метров.\n' +
      'math-time-compare: B = 12 + 7 = 19 минут; A = 18 минут; A быстрее на 19 − 18 = 1 минуту.',
    spellingAnswerKey: 'направо\nналево\nвперёд\nназад\nждать',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка русского чтения 8 слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка русского чтения 5 фраз.' },
      { taskId: 'math-turns-and-steps', answer: '17 шагов.' },
      { taskId: 'math-steps-meters', answer: '16 шагов; 32 метра.' },
      { taskId: 'math-half-route', answer: '24; 34; осталось 14 метров.' },
      { taskId: 'math-time-compare', answer: 'B = 19 мин; A быстрее на 1 минуту.' },
      { taskId: 'logic-short-fast', answer: 'Ожидание и обход могут сделать короткий путь дольше.' },
      { taskId: 'spelling-day2', answer: 'направо, налево, вперёд, назад, ждать.' },
      { taskId: 'creative-my-path', answer: '2 предложения с направлениями.' },
      { taskId: 'speech-day2', answer: 'Направления и ожидание по-русски.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита правильно сложила шаги (включая назад).',
      'Анита нашла 1/2 от 48.',
      'Анита сравнила полное время двух путей.',
      'Анита объяснила логическую ошибку.',
      'Анита написала 2 предложения про свой путь.'
    ],
    spellingCheck: 'Проверить направо, налево, вперёд, назад, ждать.',
    readingCheck: 'Главное — попытка чтения и понимание.',
    practicalCheck: 'Правильно, что даже «назад» считается шагами.',
    drawingCriteria: 'Не требуется. Достаточно таблицы и полоски маршрута.',
    commonMistakes: [
      '«Шаги назад не считаем.» Правильно: «Все шаги, включая назад, — пройденные шаги.»',
      '«Короче — значит быстрее.» Правильно: «Смотри на полное время.»',
      '«1/2 от 48 = 8.» Правильно: «1/2 = половина, значит 24.»'
    ],
    discussionPrompts: [
      'Что важнее: скорость или безопасность?',
      'Когда ждать не мешает пути?',
      'Как понять, что путь «половина»?'
    ],
    ifEasy: 'Добавить путь C: 15 минут идти + 5 минут ждать. Сравнить с A и B.',
    ifDifficult: 'Оставить только задания 3, 5 и 6; заменить 1/2 на «половину» с помощью полоски.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за ясное объяснение «короче ≠ быстрее».',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Обязательно проверьте столбец «Ждать» в таблице времени.',
    safetyNotes: 'Не рисуйте реальные адреса или семейные маршруты. Всё на бумаге, только с родителем.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'clear-time-compare', label: 'Бонус за ясное сравнение времени', amount: 1 },
      { id: 'good-my-path', label: 'Бонус за 2 предложения про путь', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-turns-and-steps',
      'math-steps-meters',
      'math-half-route',
      'math-time-compare',
      'logic-short-fast',
      'spelling-day2',
      'creative-my-path',
      'speech-day2'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-4-day-3'
};

export default day2;
